import React, { useEffect, useRef, useState } from 'react';
import { Button, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Dialog from './ActionDialog';
import { useTranslation } from 'react-i18next';
import { cancelJoinRequest, getJoinRequest } from '../services/GameService';
import SignalRService from '../services/SignalRService';

export default function JoinApprovalDialog({ request, onOutcome }) {
    const { t } = useTranslation();
    const outcome = useRef(onOutcome);
    outcome.current = onOutcome;
    const latestRequest = useRef(request);
    latestRequest.current = request;
    const requestId = request?.requestId;
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const session = useRef(null);
    useEffect(() => {
        setBusy(false);
        setError('');
        if (!requestId) return;
        const current = { active: true };
        session.current = current;
        let checking = false;
        let completed = false;
        current.finish = value => {
            if (current.active && !completed && value?.requestId === requestId && value.status !== 'Pending') {
                completed = true;
                outcome.current(value);
            }
        };
        current.handleError = err => {
            if (!current.active) return false;
            const stale = err?.response?.data?.code?.startsWith('STALE_ROOM');
            if (stale || err?.response?.status === 404) {
                current.finish({ ...latestRequest.current, status: 'Canceled', code: stale ? 'STALE_ROOM_CONFIRMATION' : 'ROOM_NOT_FOUND' });
                return true;
            }
            return false;
        };
        const check = async () => {
            if (!current.active || checking || completed) return;
            checking = true;
            try { current.finish(await getJoinRequest(requestId)); }
            catch (err) { current.handleError(err); }
            finally { checking = false; }
        };
        const changed = event => { if (event.detail?.requestId === requestId) void check(); };
        window.addEventListener('pwf-room-entry-changed', changed);
        // Account notifications work before room membership; REST remains authoritative.
        SignalRService.start().catch(() => {});
        void check();
        const timer = setInterval(check, 500);
        return () => { current.active = false; clearInterval(timer); window.removeEventListener('pwf-room-entry-changed', changed); };
    }, [requestId]); // Outcome callback stays current without restarting polling.
    const cancel = async () => {
        const current = session.current;
        if (!current?.active || busy) return;
        setBusy(true);
        setError('');
        try { current.finish(await cancelJoinRequest(requestId)); }
        catch (err) { if (current.active && !current.handleError(err)) setError(t('roomEntry.connectionError')); }
        finally { if (current.active) setBusy(false); }
    };
    return <Dialog open={!!request} fullWidth maxWidth="sm" aria-labelledby="join-approval-title">
        <DialogTitle id="join-approval-title">{t('roomEntry.waitingTitle')}</DialogTitle>
        <DialogContent>
            <p>{t('roomEntry.waitingMessage')}</p>
            <p>{t('roomEntry.timeoutInfo')}</p>
            {error && <p role="alert">{error}</p>}
        </DialogContent>
        <DialogActions><Button disabled={busy} onClick={cancel}>{t('common.cancel')}</Button></DialogActions>
    </Dialog>;
}
